import {
  FocusEvent,
  FocusEventHandler,
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import styled, { css } from 'styled-components';

import { BackdropProps, InputAdornment, TextFieldProps } from '@mui/material';
import { ClearButton } from 'app/shared/u21-ui/components/input/text-field/styles';
import { U21TextField } from 'app/shared/u21-ui/components/input/text-field/U21TextField';
import { format } from 'date-fns';
import { usePickersContext } from '@mui/x-date-pickers';
import { createPortal } from 'react-dom';
import { StyledBackdrop } from 'app/shared/u21-ui/components/input/select/styles';
import { getDOMProps } from 'app/shared/utils/react';

// This component is to be updated once we update mui core to v7
interface DateInputFieldProps {
  autoFocus?: boolean;
  backdrop?: boolean;
  backdropProps?: Omit<BackdropProps, 'open'>;
  clearable: boolean;
  dateFormat: string;
  disabled?: boolean;
  endIcon?: ReactNode;
  error?: boolean;
  id?: string;
  inputProps?: TextFieldProps['inputProps'];
  InputProps?: TextFieldProps['InputProps'];
  label?: string;
  maxDate: Date;
  minDate: Date;
  name?: string;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onChange: (rawValue: Date | undefined) => void;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onSelectedSectionsChange?: unknown; // comes from MUI X, but not used in this component
  placeholder?: string;
  required?: boolean;
  startIcon?: ReactNode;
  value?: Date;
}

export const DateInputField = forwardRef<HTMLDivElement, DateInputFieldProps>(
  (props, ref) => {
    const {
      autoFocus,
      backdrop,
      backdropProps,
      clearable,
      dateFormat,
      disabled,
      endIcon,
      error,
      id,
      inputProps,
      InputProps,
      label,
      maxDate,
      minDate,
      name,
      onBlur,
      onChange,
      onFocus,
      onSelectedSectionsChange,
      placeholder,
      required,
      startIcon,
      value,
      ...rest
    } = props;

    const internalInputRef = useRef<HTMLInputElement>(null);

    const [inputValue, setInputValue] = useState<string>('');

    const isDateValid = useCallback(
      (maybe: Date | string): boolean => {
        const date = new Date(maybe);
        if (isNaN(date.getTime())) {
          return false;
        }
        return date < maxDate && date >= minDate;
      },
      [maxDate, minDate],
    );

    const hasClearIcon = clearable && !disabled && Boolean(value);
    const valid = useMemo(() => {
      const inputValid = inputValue ? isDateValid(inputValue) : true;
      const valueValid = value ? isDateValid(value) : true;
      return inputValid && valueValid;
    }, [inputValue, isDateValid, value]);

    useEffect(() => {
      if (value) {
        if (isDateValid(value)) {
          // temporary fix since there are instances where value is string, SC-58419 for cleanup
          setInputValue(format(new Date(value), dateFormat));
        }
      } else {
        // if value is gone, and previous value was valid, clear
        // don't clear otherwise to show red error to user
        setInputValue((prevInputValue) => {
          if (isDateValid(prevInputValue)) {
            return '';
          }
          return prevInputValue;
        });
      }
    }, [dateFormat, isDateValid, value]);

    const pickersContext = usePickersContext();

    return (
      <>
        {backdrop &&
          createPortal(
            <StyledBackdrop
              invisible
              {...backdropProps}
              open={pickersContext.open}
            />,
            document.body,
          )}
        <StyledU21TextField
          autoFocus={autoFocus}
          clearable={clearable}
          disabled={disabled}
          error={error || !valid}
          id={id}
          inputProps={inputProps}
          InputProps={{
            ...InputProps,
            endAdornment: (hasClearIcon || Boolean(endIcon)) && (
              <InputAdornment position="end">
                {hasClearIcon && (
                  <ClearButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(undefined);
                      internalInputRef.current?.focus();
                    }}
                  />
                )}
                {endIcon}
              </InputAdornment>
            ),
          }}
          ref={ref}
          inputRef={internalInputRef}
          label={label}
          placeholder={placeholder}
          name={name}
          onClick={
            disabled
              ? undefined
              : (e) => {
                  pickersContext.onOpen(e);
                  // popup steals focus so use setTimeout to get it back
                  setTimeout(() => internalInputRef.current?.focus(), 0);
                }
          }
          onChange={(newValue: string = '') => {
            setInputValue(newValue);
          }}
          onBlur={(e: FocusEvent<HTMLInputElement>) => {
            onBlur?.(e);
            // use e.target.value instead of inputValue because inputValue is delayed by 300ms
            const targetValue = e.target.value;
            if (targetValue) {
              // update with input value if valid, otherwise clear existing value
              onChange(
                isDateValid(targetValue) ? new Date(targetValue) : undefined,
              );
            } else {
              onChange(undefined);
            }
          }}
          onFocus={onFocus}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              pickersContext.onClose(e);
              internalInputRef.current?.blur();
            }
          }}
          required={required}
          startIcon={startIcon}
          value={inputValue}
          {...getDOMProps(rest)}
        />
      </>
    );
  },
);

const StyledU21TextField = styled(U21TextField)`
  ${(props) =>
    !props.disabled &&
    css`
      .MuiInputBase-root {
        cursor: pointer;

        .MuiInputBase-input {
          cursor: pointer;
        }
      }
    `}
`;
